import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import {
  createExtraScheduledLesson,
  ScheduledLessonFilters,
} from '@/data/services/scheduledLessonsServices';
import { formatLessonName } from '@/utils/lesson/name';
import Lesson from '@/models/Lesson';
import alert from '@/utils/shared/alert';
import { useMutation } from '@tanstack/react-query';
import { Control, useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import RoundedButton from '@/components/ui/custom/buttons/RoundedButton';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Modal from '@/components/ui/custom/modals/Modal';
import { ScheduledLessonTypeEnum } from '@/models/ScheduledLesson';
import { scheduledLessonsQueryKeys } from '@/data/services/querykeys';
import { PropsWithChildren, useEffect } from 'react';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useListService from '@/data/hook/useListService';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { LoadingIcon } from '@/components/icons';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { ROLES } from '@/utils/roles';

interface AddExtraLessonModalProps {
  onCancel(): void;
  isVisible?: boolean;
  klassId: number;
  lesson?: Lesson;
  updateScheduledLessons?(): Promise<void>;
}

interface SelectScheduledLesson {
  scheduledLessonId: number | null;
}

export default function AddExtraLessonModal({
  onCancel,
  isVisible,
  klassId,
  lesson,
  updateScheduledLessons,
}: AddExtraLessonModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'coursesView.admin.addExtraLesson',
  });

  const { control, handleSubmit, watch, resetField, reset } =
    useForm<SelectScheduledLesson>({
      defaultValues: { scheduledLessonId: null },
    });

  const handleCreateExtraLesson = async ({
    scheduledLessonId,
  }: SelectScheduledLesson) => {
    if (scheduledLessonId == null) return;
    const type = lesson
      ? ScheduledLessonTypeEnum.EXTRA
      : ScheduledLessonTypeEnum.FREE;
    const params = { klassId, scheduledLessonId };
    const payload = { type, lessonId: lesson?.id };

    await createExtraScheduledLesson(params, payload);
  };

  const { isPending: isLoading, mutate } = useMutation(
    handleCreateExtraLesson,
    {
      async onSuccess() {
        resetField('scheduledLessonId');
        await updateScheduledLessons?.();
      },
      onError(error) {
        alert.error(getErrorMessage(error));
      },
    },
  );

  const onSubmit = (data: SelectScheduledLesson) => {
    if (!ROLES.isValidId(data.scheduledLessonId)) return;
    mutate(data);
  };

  useEffect(() => {
    if (isVisible) {
      reset({ scheduledLessonId: null });
    }
  }, [isVisible, reset]);

  const selectedId = watch('scheduledLessonId');

  return (
    <Modal visible={!!isVisible} onClose={onCancel}>
      <form
        data-testid="selectLessonForm"
        className="flex flex-col gap-6"
        onSubmit={handleSubmit(onSubmit)}
      >
        <ModalContent
          key={isVisible ? 'open' : 'closed'}
          control={control}
          klassId={klassId}
          enabled={!!isVisible && !isNaN(klassId)}
        />

        <div className="flex gap-6">
          <RoundedButton
            type="button"
            onClick={onCancel}
            text={t('cancel')}
            color="neutral"
            className="w-full"
          />
          <RoundedButton
            loading={isLoading}
            testId="submitExtraLesson"
            text={t('add')}
            color="gradient"
            disabled={!ROLES.isValidId(selectedId)}
            className="w-full"
          />
        </div>
      </form>
    </Modal>
  );
}

type ModalContentProps = {
  klassId: number;
  control: Control<SelectScheduledLesson>;
  enabled: boolean;
};

function ModalContent({ control, klassId, enabled }: ModalContentProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'coursesView.admin.addExtraLesson',
  });
  const {
    field: { onChange },
  } = useController({ control, name: 'scheduledLessonId' });

  const scheduledLessonFilters: ScheduledLessonFilters = {
    isActive: true,
    hasDone: false,
    klassId,
  };

  const {
    results: notDoneScheduledLessons,
    isFetching: isFetchingNotDoneScheduledLessons,
  } = useInfiniteService({
    ...scheduledLessonsQueryKeys.list(scheduledLessonFilters)._ctx.infinity,
  });

  const shouldGetLastLesson =
    !isFetchingNotDoneScheduledLessons && !notDoneScheduledLessons.length;

  const { results: hasDoneScheduledLessons, isFetching: isFetchingLastLesson } =
    useListService({
      ...scheduledLessonsQueryKeys.list({
        hasDone: true,
        ordering: '-datetime',
        isActive: true,
        klassId,
        pageSize: 1,
      }),
      enabled: shouldGetLastLesson,
    });

  const lastScheduledLesson = hasDoneScheduledLessons.at(0);

  const title = shouldGetLastLesson ? t('allLessonClassIsDone') : t('title');

  useEffect(() => {
    if (lastScheduledLesson) {
      onChange(lastScheduledLesson.id);
    }

    return () => onChange(null);
  }, [lastScheduledLesson, onChange]);

  return (
    <div className="flex flex-col gap-2">
      <Text
        text={title}
        className="text-primary"
        format="rubik-500"
        size="text-20"
      />

      <ConditionalRenderer
        condition={!shouldGetLastLesson}
        fallback={
          <ConditionalLoadingRender condition={!isFetchingLastLesson}>
            <div className="flex flex-col gap-1.5">
              <Text text={t('addExtraLessonQuestion')} />
              {lastScheduledLesson && (
                <Text
                  text={formatLessonName(
                    lastScheduledLesson.lesson,
                    lastScheduledLesson,
                  )}
                />
              )}
            </div>
          </ConditionalLoadingRender>
        }
      >
        <ConditionalLoadingRender
          condition={
            !isFetchingLastLesson && !isFetchingNotDoneScheduledLessons
          }
        >
          <Text text={t('selectLessonExplain')} />
          <InfiniteSearchInput
            onDeselect={() => onChange(null)}
            service={scheduledLessonsQueryKeys.list}
            onSelect={({ id }) => onChange(id)}
            displayName={scheduledLesson =>
              formatLessonName(scheduledLesson.lesson, scheduledLesson)
            }
            input={{
              testId: 'scheduledLessonSelect',
              placeholder: t('selectLesson'),
            }}
            filters={scheduledLessonFilters}
            options={{ enabled, staleTime: REQUEST_STALE_TIME_IN_MS }}
            className={{ base: 'w-full flex self-center' }}
          />
        </ConditionalLoadingRender>
      </ConditionalRenderer>
    </div>
  );
}

type ConditionalLoadingRenderProps = {
  condition: boolean;
};

function ConditionalLoadingRender({
  condition,
  children,
}: PropsWithChildren<ConditionalLoadingRenderProps>) {
  return (
    <ConditionalRenderer
      condition={condition}
      fallback={<LoadingIcon className="w-14 mx-auto text-primary" />}
    >
      {children}
    </ConditionalRenderer>
  );
}
